import classNames from 'classnames'
import { ButtonHTMLAttributes, DetailedHTMLProps, forwardRef } from 'react'
import Icon from '../icon'
import { LoadingOval } from '../spinner/Spinner'

export type ButtonProps = {
  /**
   * Is this the principal call to action on the page?
   */
  primary?: boolean
  /**
   * What background color to use
   */
  backgroundColor?: string
  /**
   * Button contents
   */
  label?: string
  /**
   * Optional click handler
   */
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  className?: string
  icon?: string
  iconSize?: number
  loading?: boolean
  size?: 'medium' | 'large' | 'small'
  variant?: 'plain-danger' | 'plain' | 'border-grey'
  iconPosition?: 'left' | 'right'
  labelClassName?: string
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>

/**
 * Primary UI component for user interaction
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      primary = false,
      backgroundColor,
      label,
      disabled,
      type,
      className,
      loading = false,
      icon,
      iconSize = 20,
      size = 'large',
      variant,
      iconPosition = 'left',
      labelClassName = '',
      ...props
    },
    ref,
  ): JSX.Element => {
    let colorTheme = ''
    let loaderColor = ''

    if (primary) {
      colorTheme = 'bg-black text-white border-black'
      loaderColor = 'white'
    } else {
      colorTheme = 'bg-transparent text-black border-black'
      loaderColor = 'black'
    }

    switch (variant) {
      case 'plain-danger':
        colorTheme = 'bg-transparent text-danger'
        loaderColor = 'black'
        break
      case 'border-grey':
        colorTheme = 'bg-transparent text-black border-light-gray'
        loaderColor = 'black'
        break
      case 'plain':
        colorTheme = 'bg-transparent text-black border-none'
        loaderColor = 'black'
        break
    }

    return (
      <button
        {...props}
        ref={ref}
        disabled={disabled}
        type={type ?? 'button'}
        className={`
        relative
        border-[1.5px]
        rounded-[8px]
        ${colorTheme}
        hover:scale-[0.98] transition-all duration-300
        disabled:scale-100 disabled:cursor-not-allowed
        disabled:opacity-100
        py-3 md:py-4 px-10
        ${size === 'small' && '!py-2 !px-3 text-xs !gap-2'}
        text-base font-normal
        flex items-center gap-4 justify-center
        ${className}
        `}
        style={{ backgroundColor }}
      >
        {icon && (
          <Icon
            id={icon}
            width={iconSize}
            height={iconSize}
            className={classNames({
              '!order-last': iconPosition === 'right',
              'opacity-0': loading,
            })}
          />
        )}
        {label && (
          <p
            className={classNames(`${labelClassName}`, {
              'opacity-0': loading,
            })}
          >
            {label}
          </p>
        )}
        {loading && (
          <div className="absolute bg-transparent inset-0 flex items-center justify-center">
            <LoadingOval
              loaderHeight="25"
              loaderWidth="25"
              color={loaderColor}
            />
          </div>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'
